import { AccessContext, AccessControl } from "@components/AccessControl/AccessControl";
import { CookieMessage } from "@components/CookieMessage/CookieMessage";
import { FootnotesSection } from "@components/FootnotesSection/FootnotesSection";
import { JMCBreadcrumb } from "@components/JMCBreadcrumb/JMCBreadcrumb";
import { JMCHtml } from "@components/JMCHtml/JMCHtml";
import { JMCLink } from "@components/JMCLink/JMCLink";
import { PageHeader } from "@components/PageHeader/PageHeader";
import { SectionImpression } from "@components/SectionImpression/SectionImpression";
import { SEO } from "@components/SEO/SEO";
import { ButtonImpression } from "@jmc/core/src/components/ButtonImpression/index";
import { CardFooter } from "@jmc/core/src/components/CardFooter/index";
import { CardImpression } from "@jmc/core/src/components/CardImpression/index";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { CSSGrid } from "@jmc/solid-design-system/src/components/atoms/CSSGrid/CSSGrid";
import { FlexibleGrid } from "@jmc/solid-design-system/src/components/atoms/FlexibleGrid/FlexibleGrid";
import { Hero } from "@jmc/solid-design-system/src/components/atoms/Hero/Hero";
import { Icon, jnjArrowRight } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { Paper } from "@jmc/solid-design-system/src/components/atoms/Paper/Paper";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Callout } from "@jmc/solid-design-system/src/components/molecules/Callout/Callout";
import { Card } from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import { Navigation } from "@jmc/solid-design-system/src/components/molecules/Navigation/Navigation";
import { Image } from "@jmc/solid-design-system/src/components/organisms/Image/Image";
import { TwoColumnLayout } from "@jmc/solid-design-system/src/components/organisms/TwoColumnLayout/TwoColumnLayout";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { mdiLock } from "@mdi/js";
import { ValidAuthResults } from "@redux/modules/authValid";
import { AccessLevel, CMSFootnotes, numLevelToAccessLevel, PageContext } from "@types";
import classnames from "classnames";
import { graphql } from "gatsby";
import { TFunction } from "i18next";
import React, { Fragment, useContext } from "react";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import { SERVICES_URL, TOV_URL } from "../../../../plugins/JMCPageCreator/Constants";
import style from "./tov.module.scss";

interface RelatedLinks {
    related_links: [TovLinksProps];
}

interface TovLinksProps {
    file_asset?: {
        file: FileAssets;
    };
    external_resource?: ExternalResource[];
}

interface FileAssets {
    filename: string;
    title: string;
    url?: {
        href: string;
    };
}

interface ExternalResource {
    title?: string;
    display_title?: string;
    url?: {
        href: string;
    };
}

export interface ToVPageProps {
    id?: string;
    menu_label?: string;
    title?: string;
    url?: string;
    summary?: string;
    content?: string;
    link?: {
        href: string;
        title: string;
    };
    locale?: string;
    headerVisual?: {
        url: string;
    };
    menu?: MenuItem[];
    pages?: ToVPageProps[];
    t?: TFunction;
    userLevel: AccessLevel;
    footnotes?: CMSFootnotes[];
    related_links?: RelatedLinks;
}

export interface MenuItem {
    url: string;
    label: string;
    lock?: boolean;
}

interface MenuProps {
    menu: MenuItem[];
    userLevel: AccessLevel;
    t?: TFunction;
    url?: string;
}

interface Props {
    userLevel?: AccessLevel;
    pageContext?: PageContext;
    t?: TFunction;
    data: {
        page: {
            uid: string;
            access_my_tov: {
                accessTitle: string;
            };
            access_control: {
                access_level: AccessLevel;
            };
        };
        fields?: { footnotes: CMSFootnotes[] };
    };
}

export const TovMenu = ({ menu, userLevel, url }: MenuProps): JSX.Element => {
    const { t } = useTranslation();

    const tovMenu = [...(menu || [])];
    tovMenu.splice(1, 0, {
        label: t("Access my ToV", { ns: "tov" }),
        url: TOV_URL + "/access",
        lock: userLevel !== AccessLevel.level3,
    });

    return (
        <div className={style.tovBackground}>
            <div className={style.tovMenu}>
                <Navigation>
                    {tovMenu.map((item, key) => (
                        <Navigation.MenuItem key={key} activePath={url === item.url} index={key}>
                            <JMCLink name={"Menu.Item"} url={item.url} external={false} commercial={true}>
                                {item.label} {item.lock && <Icon color="white" icon={mdiLock} size="small" />}
                            </JMCLink>
                        </Navigation.MenuItem>
                    ))}
                </Navigation>
            </div>
        </div>
    );
};

const filterRelatedLinks = (
    related_links: TovLinksProps[],
): Array<{ title: string; url: string; external: boolean }> => {
    const tovLinks: Array<{ title: string; url: string; external: boolean }> = [];
    related_links &&
        related_links.map((relatedLink) => {
            if (relatedLink.external_resource?.external_resource?.length > 0) {
                const externalResource = relatedLink.external_resource?.external_resource[0];
                tovLinks.push({
                    title: externalResource?.display_title || externalResource?.title,
                    url: externalResource?.url?.href,
                    external: true,
                });
            }
            if (relatedLink.file_asset !== null && relatedLink.file_asset.file !== null) {
                tovLinks.push({
                    title: relatedLink.file_asset.file.title || relatedLink.file_asset.file.filename,
                    url: relatedLink.file_asset.file.url,
                    external: false,
                });
            }
        });
    return tovLinks;
};

export const TovRelatedLinks = ({ related_links }: RelatedLinks): JSX.Element => {
    const tovLinks = filterRelatedLinks(related_links);
    const { t } = useTranslation();

    return (
        <>
            {tovLinks && tovLinks.length > 0 ? (
                <div className={style.content}>
                    <Paper
                        className={`${style.relatedlinks} ${style.sidebar}`}
                        spacing="large"
                        data-test-id="Tov.RelatedLinks"
                    >
                        <Typography variant="h4">{t("Related links", { ns: "tov" })}</Typography>
                        {tovLinks.map((link, index) => {
                            return (
                                <JMCLink
                                    name="Related Links"
                                    key={`${link.title}-${index}`}
                                    url={link.url}
                                    external={link.external}
                                >
                                    {link.title}
                                </JMCLink>
                            );
                        })}
                    </Paper>
                </div>
            ) : (
                ""
            )}
        </>
    );
};

export const TovAccessPage = ({ menu, userLevel, url, title }: ToVPageProps): JSX.Element => {
    const { t } = useTranslation();
    const locale = useLocale();
    const { jnjFullBranded } = useJnjBranding();

    const renderBreadcrumb = (): JSX.Element => {
        const paths = [
            { url: SERVICES_URL, title: t("Services", { ns: "common" }) },
            { url: TOV_URL, title: t("Transfer of Value", { ns: "common" }) },
            { url: url, title: title ?? t("Access my Transfer of Value", { ns: "tov" }) },
        ];
        return <JMCBreadcrumb paths={paths} />;
    };

    const mapLocale = (): string => {
        switch (process.env.GATSBY_COUNTRY_CODE) {
            case "BE":
                switch (locale) {
                    case "fr-be":
                        return "be_fr";
                    case "en-us":
                        return "be_nl";
                    case "nl-be":
                        return "be_nl";
                    default:
                        return "be_nl";
                }
            case "CH":
                switch (locale) {
                    case "de-ch":
                        return "ch_de";
                    case "fr-ch":
                        return "ch_fr";
                    default:
                        return "ch_de";
                }
            case "AT":
                return "at_de";
            case "BG":
                return "bg_bg";
            case "HR":
                return "hr_hr";
            case "CZ":
                return "cz_cz";
            case "EE":
                return "ee_ee";
            case "FI":
                return "fi_fi";
            case "FR":
                return "fr_fr";
            case "DE":
                return "de_de";
            case "GR":
                return "gr_el";
            case "HU":
                return "hu_hu";
            case "IE":
                return "ie_ie";
            case "IT":
                return "it_it";
            case "LV":
                return "lv_lv";
            case "LT":
                return "lt_lt";
            case "NL":
                return "nl_nl";
            case "NO":
                return "no_no";
            case "PL":
                return "pl_pl";
            case "RO":
                return "ro_ro";
            case "RU":
                return "ru_ru";
            case "RS":
                return "sb_rs";
            case "SK":
                return "sk_sk";
            case "SI":
                return "si_si";
            case "ES":
                return "es_es";
            case "SE":
                return "se_se";
            case "TR":
                return "tr_tr";
            case "GB":
                return "gb_en";
            case "UA":
                return "ua_ua";
            default:
                return `${process.env.GATSBY_COUNTRY_CODE.toLowerCase()}_${process.env.GATSBY_COUNTRY_CODE.toLowerCase()}`;
        }
    };

    const mappedLocale = mapLocale();
    return jnjFullBranded ? (
        <>
            <PageHeader
                title={title ?? t("Access my Transfer of Value", { ns: "tov" })}
                renderBreadcrumb={!jnjFullBranded ? renderBreadcrumb : undefined}
            />
            <CookieMessage>
                <div className={style["iframe-container"]}>
                    <iframe
                        src={`/${mappedLocale}/mytov`}
                        style={{ width: "100%", height: "75vh" }}
                        frameBorder="0"
                        data-test-id="iframe"
                    />
                </div>
            </CookieMessage>
        </>
    ) : (
        <>
            <TovMenu menu={menu} userLevel={userLevel} url={url} />
            <PageHeader
                title={title ?? t("Access my Transfer of Value", { ns: "tov" })}
                renderBreadcrumb={renderBreadcrumb}
                headerBackground={"primary"}
            />
            <CookieMessage>
                <iframe
                    src={`/${mappedLocale}/mytov`}
                    style={{ width: "100%", height: "75vh" }}
                    frameBorder="0"
                    data-test-id="iframe"
                />
            </CookieMessage>
        </>
    );
};

export const TovContentPage = ({
    id,
    title,
    content,
    menu,
    pages,
    userLevel,
    footnotes,
    related_links,
    url,
}: ToVPageProps): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();

    const { t } = useTranslation();

    const isHomePage = id === "tov_startpage";

    const renderBreadcrumb = (): JSX.Element => {
        const paths = [
            { url: SERVICES_URL, title: t("Services", { ns: "common" }) },
            { url: TOV_URL, title: t("Transfer of Value", { ns: "tov" }) },
        ];
        const page = pages?.find((p) => p.id === id);
        if (!isHomePage && page) {
            paths.push({ url: "", title: page.menu_label });
        }
        return <JMCBreadcrumb paths={paths} />;
    };

    const pageHeader = (
        <PageHeader
            title={<JMCHtml footnotes={footnotes}>{title}</JMCHtml>}
            marginXl
            headerSize={"auto"}
            renderBreadcrumb={!jnjFullBranded ? renderBreadcrumb : undefined}
            footnotes={footnotes}
        />
    );

    const asideContent = (): JSX.Element => {
        const tovLinks = filterRelatedLinks(related_links);

        return (
            <>
                <div className={style["aside-block"]}>
                    <Typography variant="h5" color="title">
                        {t("Quick action", { ns: "tov" })}
                    </Typography>
                    <JMCLink name="Quick action" url={`${TOV_URL}/access`}>
                        <ButtonImpression
                            name={t("My Transfer of Value", { ns: "tov" })}
                            category="secondary"
                            placement={"TOV"}
                            targeturl={`${TOV_URL}/access`}
                            targettype="internal"
                        >
                            <Button
                                color="primary"
                                size="medium"
                                id="My Transfer of Value"
                                aria-label={t("My Transfer of Value", { ns: "tov" })}
                                className={style["button-custom"]}
                            >
                                {t("My Transfer of Value", { ns: "tov" })}
                            </Button>
                        </ButtonImpression>
                    </JMCLink>
                </div>

                {tovLinks?.length > 0 && (
                    <div className={style["aside-block"]}>
                        <Typography variant="h5" color="title">
                            {t("Related links", { ns: "tov" })}
                        </Typography>
                        {tovLinks.map((link, index) => {
                            return (
                                <JMCLink
                                    name="Related Links"
                                    key={`${link.title}-${index}`}
                                    url={link.url}
                                    external={link.external}
                                >
                                    <Typography variant="label-01" underline>
                                        {link.title}
                                    </Typography>
                                </JMCLink>
                            );
                        })}
                    </div>
                )}
            </>
        );
    };

    const footer = (): JSX.Element => {
        return <>{isHomePage && <TovHomepageOverview pages={pages} />}</>;
    };

    return jnjFullBranded ? (
        <TwoColumnLayout detailContentId={id} pageHeader={pageHeader} asideContent={asideContent()} footer={footer()}>
            <div className={style["main-content"]}>
                <JMCHtml footnotes={footnotes}>{content}</JMCHtml>
            </div>
        </TwoColumnLayout>
    ) : (
        <>
            <TovMenu menu={menu} userLevel={userLevel} url={url} />
            <PageHeader
                title={<JMCHtml footnotes={footnotes}>{title}</JMCHtml>}
                renderBreadcrumb={renderBreadcrumb}
                headerBackground={"primary"}
            />
            <div className={style.body}>
                <FlexibleGrid spacing={16} minHeight={false} fractions={[2, 1]}>
                    <div className={classnames(style.content, style.firstCol)}>
                        <Paper spacing="large" data-test-id="Tov.Content">
                            {content && <JMCHtml footnotes={footnotes}>{content}</JMCHtml>}
                        </Paper>
                    </div>
                    <div className={style.secondCol}>
                        <div className={style.content}>
                            <Paper
                                className={`${style.speakers} ${style.sidebar}`}
                                spacing="large"
                                data-test-id="Tov.QuickAction"
                            >
                                <Typography variant="h4">{t("Quick action", { ns: "tov" })}</Typography>
                                <JMCLink name="Quick action" url={`${TOV_URL}/access`}>
                                    <ButtonImpression
                                        name={t("Access my Transfer of Value", { ns: "tov" })}
                                        category="secondary"
                                        placement={"TOV"}
                                        targeturl={`${TOV_URL}/access`}
                                        targettype="internal"
                                    >
                                        <Button
                                            color="secondary"
                                            endIcon={userLevel !== AccessLevel.level3 ? "lock" : null}
                                            id="Access my Transfer of Value"
                                            aria-label={t("Access my Transfer of Value", { ns: "tov" })}
                                        >
                                            {t("Access my Transfer of Value", { ns: "tov" })}
                                        </Button>
                                    </ButtonImpression>
                                </JMCLink>
                            </Paper>
                        </div>
                        <>
                            <TovRelatedLinks related_links={related_links} />
                        </>
                    </div>
                </FlexibleGrid>
            </div>
            {isHomePage && <TovHomepageOverview pages={pages} />}
            {footnotes && footnotes.length > 0 && <FootnotesSection footnotes={footnotes} />}
        </>
    );
};

export const TovHomepageOverview = ({ pages }: { pages: ToVPageProps[] }): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();

    const { t } = useTranslation();

    const whyPage = pages.find((page) => page.id === "why_a_disclosure_of_transfer_of_value");
    const theWayPage = pages.find((page) => page.id === "the-janssen-way");
    const theApproachPage = pages.find((page) => page.id === "the-janssen-approach");
    const educationPage = pages.find((page) => page.id === "educational_program");
    const calloutImage = (): JSX.Element => {
        return (
            <Image
                url={whyPage.headerVisual.url}
                alt={whyPage?.title}
                enableLightBox={false}
                className={style["callout__image"]}
            />
        );
    };

    const readMoreButton = (): JSX.Element => {
        return (
            <JMCLink name={t("Read more", { ns: "common" })} url={whyPage.url}>
                <ButtonImpression
                    name={t("Read more", { ns: "common" })}
                    category="secondary"
                    placement={"TOV"}
                    targeturl={whyPage.url}
                    targettype="internal"
                >
                    <Button
                        color="white"
                        variant="contained"
                        id="Read more"
                        aria-label={t("Read more", { ns: "common" })}
                    >
                        {t("Read more", { ns: "common" })}
                    </Button>
                </ButtonImpression>
            </JMCLink>
        );
    };

    const iconRightArrow = (): JSX.Element => {
        return <Icon type="jnj" icon={jnjArrowRight} color="primary" />;
    };

    return jnjFullBranded ? (
        <>
            <div className={style["callout"]}>
                <Callout
                    id="home_why"
                    title={whyPage.title}
                    image={calloutImage()}
                    backgroundColor="primary"
                    contentAlignment="end"
                    footer={readMoreButton()}
                    removePaddingTopImage
                >
                    <JMCHtml footnotes={whyPage.footnotes} fullLengthFootnotes>
                        {whyPage.summary}
                    </JMCHtml>
                </Callout>
            </div>
            <div className={style["home-cards"]}>
                <CSSGrid container overview data-test-id="TOV.Overview">
                    <CardImpression
                        type={`TovCard`}
                        name={"Card 0"}
                        placement={`TOV Page`}
                        position={0}
                        targeturl={theWayPage.url}
                    >
                        <Card
                            id="home_theway"
                            image={theWayPage.headerVisual.url}
                            title={
                                <JMCHtml footnotes={theWayPage.footnotes} fullLengthFootnotes>
                                    {theWayPage.title}
                                </JMCHtml>
                            }
                            isClickable
                            isTransparent
                            removeContentInlineMargin
                            footer={
                                <CardFooter>
                                    <JMCLink name={t("Read more", { ns: "common" })} url={theWayPage.url}>
                                        <ButtonImpression
                                            name={t("Read more", { ns: "common" })}
                                            category="secondary"
                                            placement={"TOV"}
                                            targeturl={theWayPage.url}
                                            targettype="internal"
                                        >
                                            <Button
                                                variant="naked"
                                                color="secondary"
                                                id="Read more"
                                                aria-label={t("Read more", { ns: "common" })}
                                                accessoryRight={iconRightArrow()}
                                            >
                                                {t("Read more", { ns: "common" })}
                                            </Button>
                                        </ButtonImpression>
                                    </JMCLink>
                                </CardFooter>
                            }
                        >
                            <JMCHtml footnotes={theWayPage.footnotes} fullLengthFootnotes>
                                {theWayPage.summary}
                            </JMCHtml>
                        </Card>
                    </CardImpression>
                    <CardImpression
                        type={`TovCard`}
                        name={"Card 1"}
                        placement={`TOV Page`}
                        position={1}
                        targeturl={theApproachPage.url}
                    >
                        <Card
                            id="home_theapproach"
                            image={theApproachPage.headerVisual.url}
                            title={
                                <JMCHtml footnotes={theApproachPage.footnotes} fullLengthFootnotes>
                                    {theApproachPage.title}
                                </JMCHtml>
                            }
                            removeContentInlineMargin
                            isClickable
                            isTransparent
                            footer={
                                <JMCLink name={t("Read more", { ns: "common" })} url={theApproachPage.url}>
                                    <ButtonImpression
                                        name={t("Read more", { ns: "common" })}
                                        category="secondary"
                                        placement={"TOV"}
                                        targeturl={theApproachPage.url}
                                        targettype="internal"
                                    >
                                        <Button
                                            variant="naked"
                                            color="secondary"
                                            id="Read more"
                                            aria-label={t("Read more", { ns: "common" })}
                                            accessoryRight={iconRightArrow()}
                                        >
                                            {t("Read more", { ns: "common" })}
                                        </Button>
                                    </ButtonImpression>
                                </JMCLink>
                            }
                        >
                            <JMCHtml footnotes={theApproachPage.footnotes} fullLengthFootnotes>
                                {theApproachPage.summary}
                            </JMCHtml>
                        </Card>
                    </CardImpression>
                    <CardImpression
                        type={`TovCard`}
                        name={"Card 2"}
                        placement={`TOV Page`}
                        position={2}
                        targeturl={theWayPage.url}
                    >
                        <Card
                            id="home_education"
                            image={educationPage.headerVisual.url}
                            title={
                                <JMCHtml footnotes={educationPage.footnotes} fullLengthFootnotes>
                                    {educationPage.title}
                                </JMCHtml>
                            }
                            removeContentInlineMargin
                            isClickable
                            isTransparent
                            footer={
                                <JMCLink name={"common:Read more"} url={educationPage.link.href} external={true}>
                                    <ButtonImpression
                                        name={t("Read more", { ns: "common" })}
                                        category="secondary"
                                        placement={"TOV"}
                                        targeturl={educationPage.link.href}
                                        targettype="internal"
                                    >
                                        <Button
                                            variant="naked"
                                            color="secondary"
                                            id="Read more"
                                            aria-label={t("Read more", { ns: "common" })}
                                            accessoryRight={iconRightArrow()}
                                        >
                                            {t("Read more", { ns: "common" })}
                                        </Button>
                                    </ButtonImpression>
                                </JMCLink>
                            }
                        >
                            <JMCHtml footnotes={educationPage.footnotes} fullLengthFootnotes>
                                {educationPage.summary}
                            </JMCHtml>
                        </Card>
                    </CardImpression>
                </CSSGrid>
            </div>
        </>
    ) : (
        <>
            <div className={style.homeHero}>
                <SectionImpression name={"Home Section"} position={0}>
                    <Hero
                        id="home_why"
                        fullWidth={true}
                        title={
                            <JMCHtml footnotes={whyPage.footnotes} fullLengthFootnotes>
                                {whyPage.title}
                            </JMCHtml>
                        }
                        image={whyPage.headerVisual.url}
                        footer={
                            <JMCLink name={t("Read more", { ns: "common" })} url={whyPage.url}>
                                <ButtonImpression
                                    name={t("Read more", { ns: "common" })}
                                    category="secondary"
                                    placement={"TOV"}
                                    targeturl={whyPage.url}
                                    targettype="internal"
                                >
                                    <Button
                                        color="secondary"
                                        id="Read more"
                                        aria-label={t("Read more", { ns: "common" })}
                                    >
                                        {t("Read more", { ns: "common" })}
                                    </Button>
                                </ButtonImpression>
                            </JMCLink>
                        }
                        marginXl
                    >
                        <div className={style.homeHero__content}>
                            <JMCHtml footnotes={whyPage.footnotes} color="white" fullLengthFootnotes>
                                {whyPage.summary}
                            </JMCHtml>
                        </div>
                    </Hero>
                </SectionImpression>
            </div>
            <div className={style.homeCards}>
                <FlexibleGrid spacing={16} minHeight={false} fractions={[1, 1, 1, 1, 1, 1]}>
                    <div className={style.gridCol}>
                        <CardImpression
                            type={`TovCard`}
                            name={"Card 0"}
                            placement={`TOV Page`}
                            position={0}
                            targeturl={theWayPage.url}
                        >
                            <Card
                                id="home_theway"
                                image={theWayPage.headerVisual.url}
                                title={
                                    <JMCHtml footnotes={theWayPage.footnotes} fullLengthFootnotes>
                                        {theWayPage.title}
                                    </JMCHtml>
                                }
                                footer={
                                    <CardFooter>
                                        <JMCLink name={t("Read more", { ns: "common" })} url={theWayPage.url}>
                                            <ButtonImpression
                                                name={t("Read more", { ns: "common" })}
                                                category="secondary"
                                                placement={"TOV"}
                                                targeturl={theWayPage.url}
                                                targettype="internal"
                                            >
                                                <Button
                                                    color="secondary"
                                                    id="Read more"
                                                    aria-label={t("Read more", { ns: "common" })}
                                                >
                                                    {t("Read more", { ns: "common" })}
                                                </Button>
                                            </ButtonImpression>
                                        </JMCLink>
                                    </CardFooter>
                                }
                            >
                                <JMCHtml footnotes={theWayPage.footnotes} fullLengthFootnotes>
                                    {theWayPage.summary}
                                </JMCHtml>
                            </Card>
                        </CardImpression>
                    </div>
                    <div className={style.gridCol}>
                        <CardImpression
                            type={`TovCard`}
                            name={"Card 1"}
                            placement={`TOV Page`}
                            position={1}
                            targeturl={theApproachPage.url}
                        >
                            <Card
                                id="home_theapproach"
                                image={theApproachPage.headerVisual.url}
                                title={
                                    <JMCHtml footnotes={theApproachPage.footnotes} fullLengthFootnotes>
                                        {theApproachPage.title}
                                    </JMCHtml>
                                }
                                footer={
                                    <JMCLink name={t("Read more", { ns: "common" })} url={theApproachPage.url}>
                                        <ButtonImpression
                                            name={t("Read more", { ns: "common" })}
                                            category="secondary"
                                            placement={"TOV"}
                                            targeturl={theApproachPage.url}
                                            targettype="internal"
                                        >
                                            <Button
                                                color="secondary"
                                                id="Read more"
                                                aria-label={t("Read more", { ns: "common" })}
                                            >
                                                {t("Read more", { ns: "common" })}
                                            </Button>
                                        </ButtonImpression>
                                    </JMCLink>
                                }
                            >
                                <JMCHtml footnotes={theApproachPage.footnotes} fullLengthFootnotes>
                                    {theApproachPage.summary}
                                </JMCHtml>
                            </Card>
                        </CardImpression>
                    </div>
                    <div className={style.gridCol}>
                        <CardImpression
                            type={`TovCard`}
                            name={"Card 2"}
                            placement={`TOV Page`}
                            position={2}
                            targeturl={theWayPage.url}
                        >
                            <Card
                                id="home_education"
                                image={educationPage.headerVisual.url}
                                title={
                                    <JMCHtml footnotes={educationPage.footnotes} fullLengthFootnotes>
                                        {educationPage.title}
                                    </JMCHtml>
                                }
                                footer={
                                    <JMCLink name={"common:Read more"} url={educationPage.link.href} external={true}>
                                        <ButtonImpression
                                            name={t("Read more", { ns: "common" })}
                                            category="secondary"
                                            placement={"TOV"}
                                            targeturl={educationPage.link.href}
                                            targettype="internal"
                                        >
                                            <Button
                                                color="secondary"
                                                id="Read more"
                                                aria-label={t("Read more", { ns: "common" })}
                                            >
                                                {t("Read more", { ns: "common" })}
                                            </Button>
                                        </ButtonImpression>
                                    </JMCLink>
                                }
                            >
                                <JMCHtml footnotes={educationPage.footnotes} fullLengthFootnotes>
                                    {educationPage.summary}
                                </JMCHtml>
                            </Card>
                        </CardImpression>
                    </div>
                </FlexibleGrid>
            </div>
        </>
    );
};

export const TovPage = ({
    userLevel,
    pageContext,
    data: {
        page: {
            uid,
            access_my_tov: { accessTitle },
            access_control: { access_level },
        },
    },
}: Props): JSX.Element => {
    const {
        isAccessPage = false,
        gatedUrl,
        publicContent,
        availableLocales,
        url,
        page: { seo_settings },
    } = pageContext || {};
    const level3 = access_level === AccessLevel.level3;
    const hreflangTags =
        availableLocales?.length > 1
            ? {
                  locale: availableLocales,
                  url: url,
              }
            : null;

    const Wrapper = (): JSX.Element => {
        const gatedContent = useContext(AccessContext);
        return (
            <>
                {level3 && (
                    <Helmet>
                        <meta name="robots" content="noindex" />
                    </Helmet>
                )}
                <TovContentPage {...gatedContent} userLevel={userLevel} url={url} />
            </>
        );
    };

    return (
        <Fragment>
            <SEO
                title={seo_settings?.meta_title}
                description={seo_settings?.meta_description}
                availableLocale={hreflangTags}
                level={isAccessPage ? AccessLevel.level3 : access_level}
                context={pageContext}
                service
                uid={uid}
                contentType="transfer_of_value"
            />
            {isAccessPage ? (
                <AccessControl
                    level={AccessLevel.level3}
                    publicContent={userLevel === AccessLevel.level3 && publicContent}
                    accessOnly={true}
                >
                    <>
                        <Helmet>
                            <meta name="robots" content="noindex" />
                        </Helmet>
                        <TovAccessPage {...publicContent} userLevel={userLevel} url={url} title={accessTitle} />
                    </>
                </AccessControl>
            ) : (
                <AccessControl gatedUrl={gatedUrl} level={access_level} publicContent={publicContent}>
                    <Wrapper />
                </AccessControl>
            )}
        </Fragment>
    );
};

export default connect(({ authValid }: { authValid: { data: ValidAuthResults } }) => ({
    userLevel: numLevelToAccessLevel(
        authValid?.data?.results?.find((v) => v.provider === "JANRAIN_HCP" || v.provider === "PING")?.level || 1,
    ),
}))(TovPage);

export const query = graphql`
    query ($locale: String!) {
        page: contentstackTransferOfValue(publish_details: { locale: { eq: $locale } }) {
            uid
            access_my_tov {
                accessTitle: title
            }
            access_control {
                access_level
            }
            imedical_review {
                imr {
                    code: title
                    date_of_preparation
                }
            }
        }
    }
`;
